export type SlideImageResult = {
  index: number;
  slideId: string;
  imageBase64: string;
};

export type AllSlideImagesResult = {
  slides: SlideImageResult[];
  selectedSlideIndex: number;
  selectedSlideId: string;
};

const SLIDE_THUMBNAIL_EXPORT_HEIGHT_PX = 1080;

function ensurePowerPointApi18() {
  const ok =
    typeof Office !== "undefined" &&
    Office.context?.requirements?.isSetSupported?.("PowerPointApi", "1.8");
  if (!ok) {
    throw new Error("This feature requires PowerPointApi 1.8+.");
  }
}

export async function getAllSlideImages(): Promise<AllSlideImagesResult> {
  ensurePowerPointApi18();

  if (typeof PowerPoint === "undefined" || typeof PowerPoint.run !== "function") {
    throw new Error("PowerPoint.run is not available.");
  }

  return await PowerPoint.run(async (context) => {
    const slides = context.presentation.slides;
    slides.load("items/id");

    const selectedSlides = context.presentation.getSelectedSlides();
    selectedSlides.load("items");

    await context.sync();

    let selectedSlideIndex = 0;
    let selectedSlideId = slides.items.length > 0 ? slides.items[0].id : "";

    if (selectedSlides.items.length > 0) {
      const activeSlide = selectedSlides.items[0];
      activeSlide.load("id");
      await context.sync();
      const activeId = activeSlide.id;
      const foundIdx = slides.items.findIndex((s) => s.id === activeId);
      if (foundIdx >= 0) {
        selectedSlideIndex = foundIdx;
        selectedSlideId = activeId;
      } else {
        selectedSlideId = activeId;
      }
    }

    const requests: Array<{ index: number; slideId: string; image: OfficeExtension.ClientResult<string> }> = [];

    for (let i = 0; i < slides.items.length; i++) {
      const slide = slides.items[i];
      const getImage = (slide as any).getImageAsBase64;
      if (typeof getImage !== "function") {
        throw new Error("getImageAsBase64 is not available in this PowerPoint version.");
      }

      const image = getImage.call(slide, { height: SLIDE_THUMBNAIL_EXPORT_HEIGHT_PX }) as OfficeExtension.ClientResult<string>;
      requests.push({ index: i, slideId: slide.id, image });
    }

    // Batch all image requests, then a single sync.
    await context.sync();

    const slideResults: SlideImageResult[] = requests.map((r) => ({
      index: r.index,
      slideId: r.slideId,
      imageBase64: r.image.value,
    }));

    if (slideResults.length > 0) {
      const safeIdx = Math.min(Math.max(0, selectedSlideIndex), slideResults.length - 1);
      const row = slideResults[safeIdx];
      if (row.slideId !== selectedSlideId) {
        const byId = slideResults.find((s) => s.slideId === selectedSlideId);
        if (byId) {
          selectedSlideIndex = byId.index;
          selectedSlideId = byId.slideId;
        } else {
          selectedSlideIndex = row.index;
          selectedSlideId = row.slideId;
        }
      }
    }

    return {
      slides: slideResults,
      selectedSlideIndex,
      selectedSlideId,
    };
  });
}

export async function goToSlideByIndexOrId(index: number, slideId?: string): Promise<void> {
  ensurePowerPointApi18();

  if (typeof PowerPoint !== "undefined" && typeof PowerPoint.run === "function") {
    try {
      await PowerPoint.run(async (context) => {
        const slides = context.presentation.slides;
        const slide =
          slideId != null && slideId !== ""
            ? (slides.getItem(slideId) as any)
            : (slides.getItemAt(index) as any);

        const setSelectedSlide = slide?.setSelectedSlide;
        if (typeof setSelectedSlide === "function") {
          setSelectedSlide.call(slide);
          await context.sync();
          return;
        }
        throw new Error("setSelectedSlide is not available.");
      });
      return;
    } catch {
      // fall through to goToByIdAsync
    }
  }

  if (slideId && Office.context?.document?.goToByIdAsync) {
    await new Promise<void>((resolve, reject) => {
      Office.context.document.goToByIdAsync(
        slideId,
        (Office as any).GoToType?.Slide ?? "slide",
        (asyncResult: Office.AsyncResult<void>) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) resolve();
          else reject(asyncResult.error ?? new Error("Failed to navigate to slide."));
        }
      );
    });
    return;
  }

  throw new Error("Unable to navigate to the selected slide in this environment.");
}
